import { memo, useMemo } from 'react';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { MandatoryAttributes, MandatoryFilter, MESSAGES, OperationMode } from 'src/modules/common/constants';
import { HomePageData } from 'src/modules/home/HomePageMapping';
import { Filter, FilterConfig } from 'src/utils/composeFilter';
import clsx from 'clsx';
import { CheckboxButton } from 'src/modules/common/components/Checkbox/CheckboxButton.tsx';
import { compareOperatingMode } from 'src/modules/home/components/compareOperatingMode.ts';
import { AttributeAlias, AttributesAliases } from 'src/generated/dict.ts';
import { isPressureDependentSelected } from 'src/modules/home/isPressureDependentSelected.ts';
import { WarningCircleFilledIcon } from 'src/modules/common/icons/WarningCircleFilledIcon';
import { Delayed } from 'src/common/Delayed/Delayed';

type Props = {
    homePageData: HomePageData;
    filter: Filter;
    userFilter: Filter;
    filterConfig: FilterConfig;
    isRevit6WayFiltered: boolean;
    onChangeHandler: (detail: { name: string; value: string }) => void;
}

export const HomeFilters = memo(({
    filterConfig,
    homePageData,
    filter,
    userFilter,
    isRevit6WayFiltered,
    onChangeHandler,
}: Props) => {
    const missingMandatoryAttributes = useMemo(() => {
        const keys = Object.keys(filter || {});
        const mandatoryAttributes = [
            ...MandatoryAttributes,
            ...(isPressureDependentSelected(filter) ? [OperationMode] : []),
        ];
        return mandatoryAttributes.filter((item) => !keys.includes(item));
    }, [filter]);

    return (
        <aside className="vss-home-filters">
            {filterConfig
                .filter((item) => {
                    const [name] = item;
                    return name !== MandatoryFilter.ValveType;
                })
                .map((item) => {
                    const [name] = item;
                    const values = homePageData[`raw_${name}` as keyof HomePageData];
                    const valuesAvailable = (homePageData[name as keyof HomePageData]?.values ?? [])
                        .map((v) => v?.value);
                    const valuesAutoSelected = filter[name] || [];
                    const valuesSelectedByUser = userFilter[name] || [];
                    let data = values.values;
                    const isError = missingMandatoryAttributes.includes(name);
                    const isValveTypeSelected = (userFilter[MandatoryFilter.ValveType] || []).length > 0;

                    if (name === 'Type') {
                        data = data.slice()
                            .sort((a, b) => b.value.localeCompare(a.value));
                    }

                    if (name === 'Fluid') {
                        data = data.slice()
                            .sort((a, b) => b.value.localeCompare(a.value));
                    }

                    if (name === OperationMode) {
                        // hide operation mode if
                        // no values available or pressure independent filter active and not touched by user
                        if (valuesAvailable.length === 0 || (
                            valuesSelectedByUser.length === 0 && !(isPressureDependentSelected(filter) &&
                                Array.isArray(filter.Pattern) && filter.Pattern.length > 0)
                        )) {
                            return '';
                        }

                        data = data.slice()
                            .sort(compareOperatingMode);
                    }

                    if (isRevit6WayFiltered && name === 'Pattern') {
                        data = data.filter(({ value }) => value !== '6-way');
                    }

                    return (
                        <div
                            key={name}
                            className={clsx({
                                'vss-home-filters__radio-group': true,
                            })}
                        >
                            <h3 className="vss-home-filters__radio-legend">
                                <I18n messageId={AttributesAliases[name as AttributeAlias]}/>
                            </h3>

                            {data.map(({ label, value }, index) => {
                                const checked = valuesAutoSelected.includes(value);
                                const isSingleVariant = valuesAvailable.length === 1 &&
                                    !valuesSelectedByUser.includes(value);
                                const allowed = valuesSelectedByUser.includes(value) ||
                                    (!checked && valuesAvailable.includes(value));
                                const selectedByUser = valuesSelectedByUser.includes(value);
                                const isDisabled = () => {
                                    switch (true) {
                                        case name === 'Type':
                                            return valuesAutoSelected.length > 0 && !selectedByUser;
                                        case name === MandatoryFilter.Fluid:
                                        case name === MandatoryFilter.Pattern:
                                            return !valuesAvailable.includes(value) ||
                                                (valuesAutoSelected.length > 0 && !selectedByUser);
                                        case name === OperationMode:
                                            return !valuesAvailable.includes(value) ||
                                                (valuesAutoSelected.length > 0 && !selectedByUser);
                                        default:
                                            return isSingleVariant || !allowed;
                                    }
                                };
                                const disabled = isDisabled();
                                if (name === OperationMode && !valuesAvailable.includes(value)) {
                                    return '';
                                }
                                return (
                                    <CheckboxButton
                                        key={`${name}_${value}`}
                                        id={`${name}_${index}`}
                                        onChange={() => onChangeHandler({ value, name })}
                                        checked={checked}
                                        disabled={disabled}
                                    >{label.substring(0, 1) === '@'
                                            ? <I18n messageId={label.replace('@', '')}/> : label}
                                    </CheckboxButton>
                                );
                            })}

                            {isValveTypeSelected && isError && (
                                <Delayed waitBeforeShow={300}>
                                    <div className="vss-home-filters__error">
                                        <div className="vss-home-filters__error-icon">
                                            <WarningCircleFilledIcon/>
                                        </div>

                                        <span className="vss-home-filters__error-text">
                                            <I18n messageId={MESSAGES.valvesHomePageFilterError}/>
                                        </span>
                                    </div>
                                </Delayed>
                            )}
                        </div>
                    );
                })}
        </aside>
    );
});
